// Where everything in the app is, and who may open it. One place for:
//   - ROUTES: what each page requires (roles, and the module that must be switched on). App.jsx guards every
//     signed-in route with it, so a page and its card cannot disagree.
//   - MODULES: the cards on Home and in the Settings hub, by section, with plain names.
// Plain data (icons are names, resolved in the components), so it can be tested without a browser.
// The server is still the authority on what anyone may do; this decides what is shown.

const HR_ADMIN = ['ADMIN', 'HR'];
const ADMIN = ['ADMIN'];

/** Who counts as what. `ctx.teamSize`: how many people report to them (at any depth), when known. */
export const access = (user = {}, ctx = {}) => {
    const role = user.role;
    const isAdmin = role === 'ADMIN';
    const isHrOrAdmin = isAdmin || role === 'HR';
    const hasTeam = (ctx.teamSize || 0) > 0;
    return {
        isAdmin,
        isHrOrAdmin,
        isFinance: role === 'FINANCE',
        hasTeam,
        isManager: role === 'MANAGER' || hasTeam,
        // The Leave page shows Team Requests by role (MANAGER, HR, ADMIN).
        approvesLeave: role === 'MANAGER' || isHrOrAdmin,
        // Expense reports: managers approve first, finance after.
        approvesExpenses: ['MANAGER', 'HR', 'ADMIN', 'FINANCE'].includes(role),
    };
};

/** What each signed-in page requires. Paths are exactly as in App.jsx. */
export const ROUTES = {
    '/dashboard': {},
    '/profile': {},
    '/help': {},
    '/help/:slug': {},
    '/bugs': {},
    '/welcome': {}, // the joining portal: always reachable before joining

    '/leaves': { amoeba: 'LEAVE' },
    '/attendance': { amoeba: 'ATTENDANCE' },
    // Anyone: the server shows only their reporting line (HR/Admin: everyone).
    '/admin/attendance': { amoeba: 'ATTENDANCE' },
    '/my-salary': { amoeba: 'PAYROLL' },
    '/admin/payroll': { amoeba: 'PAYROLL', roles: ['ADMIN', 'HR', 'FINANCE'] },

    '/tools/expenses': { amoeba: 'EXPENSE' },
    '/tools/expenses/new': { amoeba: 'EXPENSE' },
    '/tools/expenses/:id': { amoeba: 'EXPENSE' },

    // Anyone: project managers set up their own projects; the server decides who may do what.
    '/time': { amoeba: 'TIME' },
    '/time/approvals': { amoeba: 'TIME' },
    '/time/reports': { amoeba: 'TIME' },
    '/admin/time': { amoeba: 'TIME' },

    '/tasks': { amoeba: 'LIFECYCLE' },
    '/documents': { amoeba: 'LIFECYCLE' },
    '/settlements/:id': { amoeba: 'LIFECYCLE' },
    '/admin/lifecycle': { amoeba: 'LIFECYCLE', roles: HR_ADMIN },
    '/admin/checklists': { amoeba: 'LIFECYCLE', roles: HR_ADMIN },
    '/admin/documents': { amoeba: 'LIFECYCLE', roles: HR_ADMIN },

    '/tools/pdf': { amoeba: 'PDF' },
    '/merge-pdf': { amoeba: 'PDF' },
    '/split-pdf': { amoeba: 'PDF' },
    '/rotate-pdf': { amoeba: 'PDF' },
    '/rearrange-pdf': { amoeba: 'PDF' },
    '/watermark-pdf': { amoeba: 'PDF' },
    '/pdf-to-jpg': { amoeba: 'PDF' },
    '/image-to-pdf': { amoeba: 'PDF' },

    '/admin/settings': { roles: HR_ADMIN },
    '/admin/config': { roles: ADMIN },
    // Not tied to a module: an admin must always be able to manage people and sign-in.
    '/admin/users': { roles: ADMIN },
    '/admin/departments': { roles: HR_ADMIN },
    '/admin/holidays': { amoeba: 'HOLIDAY', roles: HR_ADMIN },
    '/admin/workflow': { amoeba: 'WORKFLOW', roles: HR_ADMIN },
    '/admin/logs': { roles: ADMIN },
    '/admin/usage': { roles: ADMIN },
};

/** May this person open a page with this rule? `hasAmoeba(name)` says whether a module is switched on. */
export const routeAllows = (rule, user = {}, hasAmoeba = () => true) => {
    if (!rule) return false;
    if (rule.roles && !rule.roles.includes(user.role)) return false;
    if (rule.amoeba && !hasAmoeba(rule.amoeba)) return false;
    return true;
};

/** The rule for a concrete path (with or without a query string), matching `:param` segments. */
export const routeFor = (path) => {
    const clean = String(path).split(/[?#]/)[0];
    if (ROUTES[clean]) return ROUTES[clean];
    const parts = clean.split('/');
    for (const [pattern, rule] of Object.entries(ROUTES)) {
        const p = pattern.split('/');
        if (p.length === parts.length && p.every((seg, i) => seg.startsWith(':') || seg === parts[i])) return rule;
    }
    return null;
};

export const SECTIONS = [
    { key: 'mine', title: 'My work' },
    { key: 'team', title: 'My team and approvals' },
    { key: 'people', title: 'People' },
    { key: 'payroll', title: 'Payroll' },
    { key: 'tools', title: 'Tools' },
    { key: 'admin', title: 'Administration' },
    { key: 'settings', title: 'Settings' }, // shown in the Settings hub, not on Home
];

const SETTINGS_GROUPS = ['Organisation', 'Leave and approvals', 'Time', 'Joining and leaving', 'System'];

/**
 * Cards. `amoeba`: the module that must be on for the card (may differ from the route: user management is
 * always reachable, but its card follows the HR module as before). `show(a)`: who it is for, beyond the route.
 */
export const MODULES = [
    // My work
    { key: 'leave', section: 'mine', title: 'Leave', icon: 'Calendar', path: '/leaves', amoeba: 'LEAVE',
        description: 'Your leave balances, applying for time off, and the holiday calendar.' },
    { key: 'attendance', section: 'mine', title: 'Attendance', icon: 'Clock', path: '/attendance', amoeba: 'ATTENDANCE',
        description: 'Mark today’s attendance and see your history.' },
    { key: 'timesheet', section: 'mine', title: 'Timesheet', icon: 'Timer', path: '/time', amoeba: 'TIME',
        description: 'Track time on projects with a timer or by hand, and submit your week.' },
    { key: 'expenses', section: 'mine', title: 'Expenses', icon: 'Receipt', path: '/tools/expenses', amoeba: 'EXPENSE',
        description: 'Expense reports with receipt scanning, sent for approval.' },
    { key: 'payslips', section: 'mine', title: 'Payslips', icon: 'Wallet', path: '/my-salary', amoeba: 'PAYROLL',
        description: 'Your salary history and pay slips to download.' },
    { key: 'tasks', section: 'mine', title: 'My tasks', icon: 'ListChecks', path: '/tasks', amoeba: 'LIFECYCLE',
        description: 'Joining and leaving tasks: your own paperwork, and things to do for colleagues.' },
    { key: 'documents', section: 'mine', title: 'My documents', icon: 'FileText', path: '/documents', amoeba: 'LIFECYCLE',
        description: 'Letters from HR, documents to sign, and the proofs you uploaded.' },

    // My team and approvals
    { key: 'leaveRequests', section: 'team', title: 'Leave requests', icon: 'CalendarCheck', path: '/leaves?tab=team', amoeba: 'LEAVE',
        show: a => a.approvesLeave,
        description: 'Approve or reject leave for the people you manage, including Leave Without Pay proposed by HR.' },
    { key: 'expenseApprovals', section: 'team', title: 'Expense approvals', icon: 'ReceiptText', path: '/tools/expenses?tab=approvals', amoeba: 'EXPENSE',
        show: a => a.approvesExpenses,
        description: 'Expense reports waiting for your approval.' },
    { key: 'timesheetApprovals', section: 'team', title: 'Timesheet approvals', icon: 'CheckSquare', path: '/time/approvals', amoeba: 'TIME',
        show: a => a.isHrOrAdmin || a.isManager,
        description: 'Approve or return weekly timesheets, and see who has not submitted.' },
    { key: 'teamAttendance', section: 'team', title: 'Team attendance', icon: 'Users', path: '/admin/attendance', amoeba: 'ATTENDANCE',
        show: a => !a.isHrOrAdmin && a.hasTeam,
        description: 'Daily attendance for the people in your reporting line.' },
    { key: 'timeReports', section: 'team', title: 'Time reports', icon: 'BarChart3', path: '/time/reports', amoeba: 'TIME',
        show: a => a.isHrOrAdmin || a.isManager,
        description: 'Hours by client, project, person or day, billable split, CSV export.' },

    // People (HR and Admin)
    { key: 'employees', section: 'people', title: 'Employees', icon: 'Users', path: '/admin/users', amoeba: 'HR',
        show: a => a.isAdmin,
        description: 'Add people, set roles and managers, and see the organisation chart.' },
    { key: 'joinersLeavers', section: 'people', title: 'Joiners and leavers', icon: 'ListChecks', path: '/admin/checklists', amoeba: 'LIFECYCLE',
        show: a => a.isHrOrAdmin,
        description: 'Start onboarding and exit checklists, follow progress, review submissions.' },
    { key: 'hrDocuments', section: 'people', title: 'Employee documents', icon: 'FileText', path: '/admin/documents', amoeba: 'LIFECYCLE',
        show: a => a.isHrOrAdmin,
        description: 'Issue letters, collect signatures, and keep employees’ documents on file.' },
    { key: 'leaveAdmin', section: 'people', title: 'Leave administration', icon: 'CalendarRange', path: '/leaves?tab=all', amoeba: 'LEAVE',
        show: a => a.isHrOrAdmin,
        description: 'Everyone’s leave, unexplained absences (Leave Without Pay) and leave balances.' },
    { key: 'orgAttendance', section: 'people', title: 'Attendance overview', icon: 'Clock', path: '/admin/attendance', amoeba: 'ATTENDANCE',
        show: a => a.isHrOrAdmin,
        description: 'Attendance and working hours across the organisation.' },

    // Payroll
    { key: 'runPayroll', section: 'payroll', title: 'Run payroll', icon: 'IndianRupee', path: '/admin/payroll', amoeba: 'PAYROLL',
        show: a => a.isHrOrAdmin || a.isFinance,
        description: 'Calculate the month’s salaries, approve them, and download reports and payslips.' },

    // Tools
    { key: 'pdf', section: 'tools', title: 'PDF tools', icon: 'FileStack', path: '/tools/pdf', amoeba: 'PDF',
        description: 'Merge, split, rotate, watermark and convert PDFs. Your files stay in your browser.' },

    // Administration (one card that opens the Settings hub)
    { key: 'settings', section: 'admin', title: 'Settings', icon: 'Settings', path: '/admin/settings',
        show: (a, user, hasAmoeba) => settingsGroups(user, hasAmoeba).length > 0,
        description: 'Organisation, departments, holidays, approval workflows, time tracking and joining and leaving setup.' },
    { key: 'usage', section: 'settings', group: 'System', title: 'Usage insights', icon: 'BarChart3', path: '/admin/usage',
        show: a => a.isAdmin,
        description: 'How many people use the app, which parts, and when. Counts only.' },

    // Settings (listed in the Settings hub)
    { key: 'orgConfig', section: 'settings', group: 'Organisation', title: 'Organisation settings', icon: 'Building2', path: '/admin/config',
        show: a => a.isAdmin,
        description: 'Name, logo, appearance, sign-in methods and single sign-on.' },
    { key: 'departments', section: 'settings', group: 'Organisation', title: 'Departments', icon: 'Network', path: '/admin/departments',
        show: a => a.isHrOrAdmin,
        description: 'The departments people belong to, and each department’s approvers.' },
    { key: 'holidays', section: 'settings', group: 'Organisation', title: 'Holidays', icon: 'CalendarDays', path: '/admin/holidays', amoeba: 'HOLIDAY',
        show: a => a.isHrOrAdmin,
        description: 'The company holiday calendar everyone sees.' },
    { key: 'leaveWorkflow', section: 'settings', group: 'Leave and approvals', title: 'Leave approvals and emails', icon: 'Workflow', path: '/admin/workflow', amoeba: 'WORKFLOW',
        show: a => a.isHrOrAdmin,
        description: 'Who approves leave, and which emails are sent.' },
    { key: 'timeSetup', section: 'settings', group: 'Time', title: 'Time tracking setup', icon: 'FolderKanban', path: '/admin/time', amoeba: 'TIME',
        show: a => a.isHrOrAdmin,
        description: 'Clients, projects, project managers and how time is entered.' },
    { key: 'lifecycleSetup', section: 'settings', group: 'Joining and leaving', title: 'Joining and leaving setup', icon: 'SlidersHorizontal', path: '/admin/lifecycle', amoeba: 'LIFECYCLE',
        show: a => a.isHrOrAdmin,
        description: 'Notice periods, settlement rules, approval flows, checklists, forms and letter templates.' },
    { key: 'systemLogs', section: 'settings', group: 'System', title: 'System logs', icon: 'ScrollText', path: '/admin/logs',
        show: a => a.isAdmin,
        description: 'Errors and the emails the app has sent.' },
];

const cardAllowed = (m, user, ctx, hasAmoeba) => {
    if (m.amoeba && !hasAmoeba(m.amoeba)) return false;
    if (!routeAllows(routeFor(m.path), user, hasAmoeba)) return false;
    return m.show ? !!m.show(access(user, ctx), user, hasAmoeba) : true;
};

/** Every card this person may see (Home and Settings hub), in registry order. */
export const visibleModules = (user = {}, ctx = {}, hasAmoeba = () => true) =>
    MODULES.filter(m => cardAllowed(m, user, ctx, hasAmoeba));

/** Home: the sections with their cards, empty sections left out. */
export const dashboardSections = (user, ctx, hasAmoeba) => {
    const cards = visibleModules(user, ctx, hasAmoeba).filter(m => m.section !== 'settings');
    return SECTIONS.filter(s => s.key !== 'settings')
        .map(s => ({ ...s, items: cards.filter(m => m.section === s.key) }))
        .filter(s => s.items.length > 0);
};

/** The Settings hub: groups in a fixed order, empty groups left out. */
export function settingsGroups(user = {}, hasAmoeba = () => true) {
    const items = MODULES.filter(m => m.section === 'settings' && cardAllowed(m, user, {}, hasAmoeba));
    return SETTINGS_GROUPS.map(title => ({ title, items: items.filter(m => m.group === title) })).filter(g => g.items.length > 0);
}

const SECTION_TITLE = Object.fromEntries(SECTIONS.map(s => [s.key, s.title]));

/**
 * The breadcrumb for a page that is a card in the registry: Home, then its section (a settings page links
 * through the Settings hub; any other section is a plain label, since there is no page for the section itself),
 * then the page's own title. An unrecognised key still gets a usable trail back to Home.
 */
export function crumbsFor(moduleKey) {
    const m = MODULES.find(mm => mm.key === moduleKey);
    const home = { label: 'Home', to: '/dashboard' };
    if (!m) return [home];
    const middle = m.section === 'settings' ? { label: 'Settings', to: '/admin/settings' } : { label: SECTION_TITLE[m.section] };
    return [home, middle, { label: m.title }];
}
